import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Sparkles, Gift, Bell, Star, Zap, Mail, BookOpen, Terminal, Search, LogOut, Lightbulb, GraduationCap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// Types
interface PromotionCTA {
  text: string;
  link?: string;
  action?: 'dismiss' | 'subscribe' | 'custom';
  style: 'primary' | 'secondary' | 'ghost' | 'link' | 'icon';
}

interface PromotionConditions {
  auth_required?: boolean;
  first_visit_only?: boolean;
  user_plans?: string[];
  max_impressions?: number;
  delay_seconds?: number;
  exit_intent?: boolean;
  tool_context?: string;
  trigger_element?: string;
}

interface MobileConfig {
  enabled: boolean;
  position?: string;
  fullWidth?: boolean;
}

interface Promotion {
  id: string;
  name: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  element_type: string;
  style_preset: string;
  custom_styles: Record<string, unknown>;
  icon: string | null;
  image_url: string | null;
  cta_buttons: PromotionCTA[];
  placements: string[];
  position: string;
  conditions: PromotionConditions;
  delay_ms: number;
  duration_ms: number | null;
  is_dismissible: boolean;
  persist_dismiss: boolean;
  animation: string;
  mobile_config: MobileConfig;
  priority: number;
}

// Icon mapping
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  sparkles: Sparkles,
  gift: Gift,
  bell: Bell,
  star: Star,
  zap: Zap,
  mail: Mail,
  'book-open': BookOpen,
  terminal: Terminal,
  search: Search,
  'log-out': LogOut,
  lightbulb: Lightbulb,
  'graduation-cap': GraduationCap,
  'hand-wave': Sparkles,
};

// Style presets
const STYLE_PRESETS: Record<string, string> = {
  default: 'bg-card border-border',
  glass_dark: 'bg-background/80 backdrop-blur-xl border-border/50',
  glass_blur: 'bg-background/60 backdrop-blur-2xl border-white/10',
  gradient_primary: 'bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border-primary/30',
  gradient_gold: 'bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-orange-500/20 border-amber-500/30',
  card_elevated: 'bg-card border-border shadow-xl',
  subtle: 'bg-muted/50 border-border/30',
  badge_primary: 'bg-primary text-primary-foreground',
  dark: 'bg-zinc-900 border-zinc-800 text-white',
  minimal: 'bg-transparent border-none',
};

// Animation variants
const getAnimation = (type: string) => {
  switch (type) {
    case 'slide':
      return {
        initial: { opacity: 0, y: -20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
      };
    case 'scale':
      return {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
      };
    case 'bounce':
      return {
        initial: { opacity: 0, scale: 0.5 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.5 },
        transition: { type: 'spring' as const, bounce: 0.5 },
      };
    default:
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      };
  }
};

// Storage helpers
const STORAGE_KEY = 'lowkeyx_promo_dismissed';
const IMPRESSIONS_KEY = 'lowkeyx_promo_impressions';

const getDismissed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const setDismissed = (ids: string[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
};

const getImpressions = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(IMPRESSIONS_KEY) || '{}');
  } catch {
    return {};
  }
};

const incrementImpressions = (id: string) => {
  const impressions = getImpressions();
  impressions[id] = (impressions[id] || 0) + 1;
  localStorage.setItem(IMPRESSIONS_KEY, JSON.stringify(impressions));
  return impressions[id];
};

// Parse DB response to Promotion
const parsePromotion = (data: Record<string, unknown>): Promotion => {
  return {
    id: data.id as string,
    name: data.name as string,
    title: data.title as string,
    subtitle: data.subtitle as string | null,
    description: data.description as string | null,
    element_type: data.element_type as string,
    style_preset: data.style_preset as string,
    custom_styles: (data.custom_styles as Record<string, unknown>) || {},
    icon: data.icon as string | null,
    image_url: data.image_url as string | null,
    cta_buttons: (data.cta_buttons as PromotionCTA[]) || [],
    placements: (data.placements as string[]) || [],
    position: data.position as string,
    conditions: (data.conditions as PromotionConditions) || {},
    delay_ms: (data.delay_ms as number) || 0,
    duration_ms: data.duration_ms as number | null,
    is_dismissible: data.is_dismissible as boolean,
    persist_dismiss: data.persist_dismiss as boolean,
    animation: data.animation as string,
    mobile_config: (data.mobile_config as MobileConfig) || { enabled: true },
    priority: (data.priority as number) || 0,
  };
};

// Hook for fetching promotions
export const usePromotions = (placement: string) => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from('promotions')
          .select('*')
          .eq('is_active', true)
          .contains('placements', [placement])
          .or(`starts_at.is.null,starts_at.lte.${now}`)
          .or(`ends_at.is.null,ends_at.gte.${now}`)
          .order('priority', { ascending: false });

        if (error) throw error;

        const dismissed = getDismissed();
        const impressions = getImpressions();
        const isFirstVisit = !localStorage.getItem('lowkeyx_visited');
        
        if (!localStorage.getItem('lowkeyx_visited')) {
          localStorage.setItem('lowkeyx_visited', 'true');
        }

        // Parse and filter
        const parsed = (data || []).map(d => parsePromotion(d as Record<string, unknown>));
        
        const filtered = parsed.filter((promo) => {
          if (promo.persist_dismiss && dismissed.includes(promo.id)) return false;
          if (isMobile && !promo.mobile_config?.enabled) return false;
          
          const conditions = promo.conditions || {};
          if (conditions.auth_required && !user) return false;
          if (conditions.first_visit_only && !isFirstVisit) return false;
          
          if (conditions.user_plans?.length && profile) {
            if (!conditions.user_plans.includes(profile.plan)) return false;
          }
          
          if (conditions.max_impressions) {
            const count = impressions[promo.id] || 0;
            if (count >= conditions.max_impressions) return false;
          }
          
          return true;
        });

        setPromotions(filtered);
      } catch (error) {
        console.error('Error fetching promotions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPromotions();
  }, [placement, user, profile, isMobile]);

  const dismiss = useCallback((id: string, persist: boolean) => {
    setPromotions(prev => prev.filter(p => p.id !== id));
    if (persist) {
      setDismissed([...getDismissed(), id]);
    }
  }, []);

  const trackImpression = useCallback((id: string) => {
    incrementImpressions(id);
  }, []);

  return { promotions, loading, dismiss, trackImpression };
};

// Base CTA Button component
const CTAButton = ({ cta, onDismiss }: { cta: PromotionCTA; onDismiss?: () => void }) => {
  const handleClick = () => {
    if (cta.action === 'dismiss' && onDismiss) {
      onDismiss();
    }
  };

  if (cta.style === 'link') {
    return cta.link ? (
      <Link to={cta.link} className="text-sm text-primary hover:underline inline-flex items-center gap-1">
        {cta.text}
        <ArrowRight className="w-3 h-3" />
      </Link>
    ) : null;
  }

  const button = (
    <Button
      size="sm"
      variant={cta.style === 'primary' ? 'default' : cta.style === 'ghost' ? 'ghost' : 'outline'}
      onClick={handleClick}
      className={cn(
        cta.style === 'primary' && 'bg-primary hover:bg-primary/90',
        "h-8 px-4 text-xs font-medium rounded-lg"
      )}
    >
      {cta.text}
      {cta.link && <ArrowRight className="w-3 h-3 ml-1" />}
    </Button>
  );

  return cta.link ? <Link to={cta.link}>{button}</Link> : button;
};

// Modal Promotion
const ModalPromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || Sparkles : Sparkles;
  const isMobile = useIsMobile();
  const anim = getAnimation(promo.animation);
  
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget && promo.is_dismissible) onDismiss();
      }}
    >
      <motion.div
        className={cn(
          "relative w-full max-w-md rounded-2xl border p-6 shadow-2xl",
          STYLE_PRESETS[promo.style_preset] || STYLE_PRESETS.default,
          isMobile && "max-w-[95vw]"
        )}
        initial={anim.initial}
        animate={anim.animate}
        exit={anim.exit}
      >
        {promo.is_dismissible && (
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
        
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-4">
            <Icon className="w-7 h-7 text-primary" />
          </div>
          
          <h3 className="text-xl font-bold mb-1">{promo.title}</h3>
          {promo.subtitle && (
            <p className="text-sm text-muted-foreground mb-2">{promo.subtitle}</p>
          )}
          {promo.description && (
            <p className="text-sm text-muted-foreground mb-6">{promo.description}</p>
          )}
          
          {promo.cta_buttons?.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {promo.cta_buttons.map((cta, i) => (
                <CTAButton key={i} cta={cta} onDismiss={onDismiss} />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// Header Bar
const HeaderBarPromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || Bell : Bell;
  const anim = getAnimation('slide');
  
  return (
    <motion.div
      className={cn(
        "w-full py-2 px-4 border-b",
        STYLE_PRESETS[promo.style_preset] || STYLE_PRESETS.default
      )}
      initial={anim.initial}
      animate={anim.animate}
      exit={anim.exit}
    >
      <div className="container mx-auto flex items-center justify-center gap-3 text-sm">
        <Icon className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="font-medium">{promo.title}</span>
        {promo.description && (
          <span className="text-muted-foreground hidden sm:inline">— {promo.description}</span>
        )}
        {promo.cta_buttons?.length > 0 && (
          <div className="flex gap-2">
            {promo.cta_buttons.map((cta, i) => (
              <CTAButton key={i} cta={cta} onDismiss={onDismiss} />
            ))}
          </div>
        )}
        {promo.is_dismissible && (
          <button onClick={onDismiss} className="ml-2 p-1 hover:bg-muted rounded">
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Banner
const BannerPromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || Sparkles : Sparkles;
  const anim = getAnimation(promo.animation);
  
  return (
    <motion.div
      className={cn(
        "relative rounded-2xl border p-4 sm:p-5 overflow-hidden",
        STYLE_PRESETS[promo.style_preset] || STYLE_PRESETS.default
      )}
      initial={anim.initial}
      animate={anim.animate}
      exit={anim.exit}
    >
      <div className="flex items-start gap-4 relative z-10">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm sm:text-base mb-0.5">{promo.title}</h3>
          {promo.subtitle && (
            <p className="text-xs text-primary/80 mb-1">{promo.subtitle}</p>
          )}
          {promo.description && (
            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{promo.description}</p>
          )}
          
          {promo.cta_buttons?.length > 0 && (
            <div className="flex gap-2 mt-3">
              {promo.cta_buttons.map((cta, i) => (
                <CTAButton key={i} cta={cta} onDismiss={onDismiss} />
              ))}
            </div>
          )}
        </div>
        
        {promo.is_dismissible && (
          <button onClick={onDismiss} className="p-1.5 rounded-lg hover:bg-background/50 transition-colors flex-shrink-0">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Inline
const InlinePromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || Lightbulb : Lightbulb;
  const anim = getAnimation('fade');
  
  return (
    <motion.div
      className={cn(
        "rounded-xl border p-3 sm:p-4",
        STYLE_PRESETS[promo.style_preset] || STYLE_PRESETS.subtle
      )}
      initial={anim.initial}
      animate={anim.animate}
      exit={anim.exit}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium">{promo.title}</p>
          {promo.description && (
            <p className="text-xs text-muted-foreground mt-1">{promo.description}</p>
          )}
        </div>
        {promo.is_dismissible && (
          <button onClick={onDismiss} className="p-1 hover:bg-muted rounded">
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Floating Badge
const FloatingPromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || Zap : Zap;
  const isMobile = useIsMobile();
  const mobilePos = promo.mobile_config?.position || 'bottom-right';
  const position = isMobile ? mobilePos : promo.position;
  
  const positionClasses: Record<string, string> = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4',
  };

  const anim = getAnimation('scale');

  return createPortal(
    <motion.div
      className={cn(
        "fixed z-50",
        positionClasses[position] || positionClasses['bottom-right']
      )}
      initial={anim.initial}
      animate={anim.animate}
      exit={anim.exit}
    >
      <div className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border",
        STYLE_PRESETS[promo.style_preset] || 'bg-primary text-primary-foreground'
      )}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{promo.title}</span>
        {promo.description && (
          <span className="text-xs opacity-80 hidden sm:inline">{promo.description}</span>
        )}
        {promo.is_dismissible && (
          <button onClick={onDismiss} className="ml-1 p-0.5 hover:bg-white/20 rounded-full">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </motion.div>,
    document.body
  );
};

// Card
const CardPromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || BookOpen : BookOpen;
  const anim = getAnimation('fade');
  
  return (
    <motion.div
      className={cn(
        "rounded-lg border p-3 hover:border-primary/50 transition-colors",
        STYLE_PRESETS[promo.style_preset] || STYLE_PRESETS.default
      )}
      initial={anim.initial}
      animate={anim.animate}
      exit={anim.exit}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{promo.title}</p>
          {promo.description && (
            <p className="text-xs text-muted-foreground truncate">{promo.description}</p>
          )}
        </div>
        {promo.cta_buttons?.length > 0 && promo.cta_buttons[0].link && (
          <Link to={promo.cta_buttons[0].link} className="text-xs text-primary hover:underline flex-shrink-0">
            {promo.cta_buttons[0].text || 'Ver'}
            <ArrowRight className="w-3 h-3 inline ml-1" />
          </Link>
        )}
      </div>
    </motion.div>
  );
};

// Sidebar
const SidebarPromotion = ({ promo, onDismiss }: { promo: Promotion; onDismiss: () => void }) => {
  const Icon = promo.icon ? ICONS[promo.icon] || Sparkles : Sparkles;
  const anim = getAnimation('fade');
  
  return (
    <motion.div
      className={cn(
        "relative rounded-xl border p-4 space-y-3",
        STYLE_PRESETS[promo.style_preset] || STYLE_PRESETS.card_elevated
      )}
      initial={anim.initial}
      animate={anim.animate}
      exit={anim.exit}
    >
      {promo.is_dismissible && (
        <button onClick={onDismiss} className="absolute top-2 right-2 p-1 hover:bg-muted rounded">
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      )}
      
      <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      
      <div>
        <h4 className="font-semibold text-sm">{promo.title}</h4>
        {promo.subtitle && (
          <p className="text-xs text-primary/80">{promo.subtitle}</p>
        )}
      </div>
      
      {promo.description && (
        <p className="text-xs text-muted-foreground">{promo.description}</p>
      )}
      
      {promo.cta_buttons?.length > 0 && (
        <div className="pt-1">
          {promo.cta_buttons.map((cta, i) => (
            <CTAButton key={i} cta={cta} onDismiss={onDismiss} />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Component mapper
const PROMOTION_COMPONENTS: Record<string, React.ComponentType<{ promo: Promotion; onDismiss: () => void }>> = {
  modal: ModalPromotion,
  header_bar: HeaderBarPromotion,
  banner: BannerPromotion,
  inline: InlinePromotion,
  floating: FloatingPromotion,
  card: CardPromotion,
  sidebar: SidebarPromotion,
  toast: InlinePromotion,
  tooltip: InlinePromotion,
};

// Main PromotionRenderer
interface PromotionRendererProps {
  placement: string;
  className?: string;
  filter?: (promo: Promotion) => boolean;
}

export const PromotionRenderer = ({ placement, className, filter }: PromotionRendererProps) => {
  const { promotions, dismiss, trackImpression } = usePromotions(placement);
  const [visiblePromos, setVisiblePromos] = useState<Promotion[]>([]);
  const [delayedPromos, setDelayedPromos] = useState<Set<string>>(new Set());

  useEffect(() => {
    promotions.forEach(promo => {
      const delay = promo.conditions?.delay_seconds ? promo.conditions.delay_seconds * 1000 : promo.delay_ms;
      
      if (delay > 0 && !delayedPromos.has(promo.id)) {
        setTimeout(() => {
          setDelayedPromos(prev => new Set([...prev, promo.id]));
          trackImpression(promo.id);
        }, delay);
      } else if (delay === 0 && !delayedPromos.has(promo.id)) {
        setDelayedPromos(prev => new Set([...prev, promo.id]));
        trackImpression(promo.id);
      }
    });
  }, [promotions, trackImpression, delayedPromos]);

  useEffect(() => {
    let visible = promotions.filter(p => delayedPromos.has(p.id));
    if (filter) {
      visible = visible.filter(filter);
    }
    setVisiblePromos(visible);
  }, [promotions, delayedPromos, filter]);

  const handleDismiss = (promo: Promotion) => {
    dismiss(promo.id, promo.persist_dismiss);
  };

  const modalPromos = visiblePromos.filter(p => p.element_type === 'modal');
  const floatingPromos = visiblePromos.filter(p => p.element_type === 'floating');
  const headerPromos = visiblePromos.filter(p => p.element_type === 'header_bar');
  const inlinePromos = visiblePromos.filter(p => !['modal', 'floating', 'header_bar'].includes(p.element_type));

  return (
    <>
      <AnimatePresence>
        {modalPromos.slice(0, 1).map(promo => (
          <ModalPromotion key={promo.id} promo={promo} onDismiss={() => handleDismiss(promo)} />
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {floatingPromos.map(promo => (
          <FloatingPromotion key={promo.id} promo={promo} onDismiss={() => handleDismiss(promo)} />
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {headerPromos.map(promo => (
          <HeaderBarPromotion key={promo.id} promo={promo} onDismiss={() => handleDismiss(promo)} />
        ))}
      </AnimatePresence>

      {inlinePromos.length > 0 && (
        <div className={cn('space-y-3', className)}>
          <AnimatePresence>
            {inlinePromos.map(promo => {
              const Component = PROMOTION_COMPONENTS[promo.element_type] || BannerPromotion;
              return (
                <Component key={promo.id} promo={promo} onDismiss={() => handleDismiss(promo)} />
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </>
  );
};

// Legacy export for backwards compatibility
export const PromotionBanner = ({ placement, className }: { placement: string; className?: string }) => {
  return <PromotionRenderer placement={placement} className={className} />;
};
